import React, { useEffect } from "react";
import { ArrowLeft, Play, Calendar, Tag, ExternalLink } from "lucide-react";
import { showColor, shortShowLabel } from "@/lib/showColors";

// ─── TapeDetail ───────────────────────────────────────────────────────
// TMDb-style single-tape landing page. Big backdrop hero, poster on the
// left, title/year/genre/synopsis/scores/CTA on the middle, and an
// episodes sidebar on the right showing every other tape on the same
// channel. Reached from ChannelBrowse tile clicks (or from the guide's
// SpotlightHero "MORE INFO" affordance).
//
// Cross-tape nav: clicking an episode in the sidebar swaps the whole
// page to that tape's detail (calls back into onOpenTape with the new
// id). WATCH NOW tunes to that tape and closes the detail view.
export default function TapeDetail({
  video,
  channel,
  siblings,
  currentPlayingId,
  onBack,
  onTune,
  onOpenTape,
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onBack?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  if (!video || !channel) {
    return (
      <div className="absolute inset-0 z-40 bg-black text-[var(--phosphor)] flex items-center justify-center" data-testid="tape-detail-empty">
        <div className="text-center">
          <div className="text-sm tracking-[0.3em] opacity-70 mb-3">— TAPE UNAVAILABLE —</div>
          <button
            type="button"
            onClick={onBack}
            className="px-3 py-1.5 border border-[var(--phosphor)] text-xs tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors"
          >
            ← BACK
          </button>
        </div>
      </div>
    );
  }

  const backdrop =
    video.backdrop_url ||
    channel.thumbnail_url ||
    channel.guide_poster_url ||
    null;
  const poster =
    video.poster_url ||
    video.backdrop_url ||
    channel.thumbnail_url ||
    null;
  const list = siblings || [];
  const idx = Math.max(0, list.findIndex((v) => v.id === video.id));
  const numberLabel = channel.numberLabel || String(channel.number || "").padStart(2, "0");

  return (
    <div
      className="absolute inset-0 z-40 bg-black text-[var(--phosphor)] overflow-y-auto scrollbar-crt"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
      data-testid="tape-detail"
    >
      {/* BACKDROP HERO */}
      <div className="relative w-full" style={{ height: "clamp(280px, 46vh, 560px)" }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 30% 40%, color-mix(in srgb, var(--phosphor) 22%, transparent) 0%, transparent 60%), linear-gradient(135deg, #050a05, #0a1408)",
          }}
        />
        {backdrop && (
          <img
            src={backdrop}
            alt={video.title}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,1) 100%)" }}
        />
        <div className="absolute inset-0 pointer-events-none scanlines opacity-55" />

        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          data-testid="detail-back"
          className="absolute top-4 left-4 md:top-6 md:left-6 flex items-center gap-2 border border-[var(--phosphor)] bg-black/70 text-[var(--phosphor)] px-3 py-1.5 text-[11px] tracking-[0.3em] hover:bg-[var(--phosphor)] hover:text-black transition-colors"
        >
          <ArrowLeft size={14} />
          BACK
        </button>
      </div>

      {/* MAIN BODY — poster + info on the left/middle, episodes list on the right */}
      <div className="relative px-6 md:px-12 -mt-32 md:-mt-40 pb-10 md:pb-14 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_420px] gap-6 md:gap-10">
        {/* LEFT: poster + copy */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          {/* Poster */}
          <div className="shrink-0 w-40 md:w-56 xl:w-64 aspect-[2/3] bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] overflow-hidden self-start shadow-[0_10px_40px_rgba(0,0,0,0.75)]" data-testid="detail-poster">
            {poster ? (
              <img
                src={poster}
                alt={video.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[10px] opacity-50 tracking-widest px-2 text-center">
                NO POSTER
              </div>
            )}
          </div>

          {/* Copy */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] opacity-85 mb-2">
              <span className="opacity-90">CH {numberLabel}</span>
              <span className="opacity-50">·</span>
              <span className="truncate max-w-[240px]">{channel.name}</span>
              {idx >= 0 && (
                <>
                  <span className="opacity-50">·</span>
                  <span>EPISODE {String(idx + 1).padStart(2, "0")}</span>
                </>
              )}
            </div>
            <div
              className="text-3xl md:text-5xl xl:text-6xl font-bold tracking-[0.02em] text-white leading-tight break-words"
              style={{ fontFamily: "VT323, monospace", textShadow: "0 2px 8px rgba(0,0,0,0.85)" }}
              data-testid="detail-title"
            >
              {video.title || "UNTITLED TAPE"}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 md:gap-3 text-[11px] tracking-[0.2em] text-white/80">
              {video.year && (
                <span className="inline-flex items-center gap-1 border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] px-2 py-0.5">
                  <Calendar size={11} />
                  {video.year}
                </span>
              )}
              {video.genre && (
                <span className="inline-flex items-center gap-1 border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] px-2 py-0.5">
                  <Tag size={11} />
                  {video.genre}
                </span>
              )}
              {video.rt_score != null && (
                <span
                  className="inline-flex items-center gap-1 border border-[#FFCC00]/60 text-[#FFCC00] px-2 py-0.5 tabular-nums"
                  title="Rotten Tomatoes"
                >
                  🍅 {video.rt_score}%
                </span>
              )}
              {video.imdb_id && (
                <a
                  href={`https://www.imdb.com/title/${video.imdb_id}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 border border-[#F5C518]/60 text-[#F5C518] px-2 py-0.5 hover:bg-[#F5C518] hover:text-black transition-colors"
                  title="Open on IMDb"
                >
                  IMDb
                  <ExternalLink size={10} />
                </a>
              )}
            </div>

            {/* Synopsis */}
            {(video.description || channel.description) && (
              <>
                <div className="text-[10px] tracking-[0.4em] opacity-70 mt-6 mb-1">OVERVIEW</div>
                <div
                  className="text-sm md:text-base text-white/90 leading-relaxed max-w-3xl"
                  data-testid="detail-synopsis"
                >
                  {video.description || channel.description}
                </div>
              </>
            )}

            {/* CTAs */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => onTune?.(video.id)}
                data-testid="detail-watch-now"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--phosphor)] text-black text-sm tracking-[0.25em] font-bold hover:brightness-110 transition-all shadow-[0_0_18px_var(--phosphor)]"
              >
                <Play size={16} fill="currentColor" />
                WATCH NOW
              </button>
              {currentPlayingId === video.id && (
                <span className="text-[10px] tracking-[0.35em] text-[var(--phosphor)] on-air-pulse">
                  ● ON AIR NOW
                </span>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: episodes list */}
        {list.length > 0 && (
          <div
            className="lg:sticky lg:top-6 lg:self-start bg-black/70 border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-3 md:p-4"
            data-testid="detail-episodes-panel"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.4em] opacity-85 phosphor-glow">
                ┌─ EPISODES ─┐
              </div>
              <div className="text-[10px] opacity-60 tabular-nums">
                {list.length}
              </div>
            </div>
            <div className="flex flex-col gap-1 max-h-[420px] lg:max-h-[560px] overflow-y-auto scrollbar-crt pr-1">
              {list.map((v, i) => {
                const active = v.id === video.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => onOpenTape?.(v.id)}
                    data-testid={`detail-episode-${i}`}
                    className={`text-left px-2 py-2 text-[11px] transition-colors flex items-start gap-2 ${
                      active
                        ? "bg-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] border border-[var(--phosphor)] text-[var(--phosphor)]"
                        : "border border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)] text-[color-mix(in_srgb,var(--phosphor)_85%,transparent)]"
                    }`}
                  >
                    <span className="tabular-nums shrink-0 w-6 opacity-70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 min-w-0 leading-snug break-words">
                      {v.title || "UNTITLED"}
                      {v.show_title && (() => {
                        const c = showColor(v.show_title);
                        return (
                          <span
                            className="ml-1 inline-block text-[8px] px-1 py-0.5 tracking-wider border font-medium align-middle"
                            style={{ color: c.color, borderColor: c.border, background: c.bg }}
                            title={v.show_title}
                          >
                            {shortShowLabel(v.show_title, 20)}
                          </span>
                        );
                      })()}
                    </span>
                    {active && <span className="text-[9px] tracking-widest shrink-0">▶</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
